import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StickyHeader from '../../components/ui/StickyHeader';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import RandomContactCard from './components/RandomContactCard';
import MessageComposer from './components/MessageComposer';
import RandomContactHistory from './components/RandomContactHistory';
import FilterControls from './components/FilterControls';
import ContactStats from './components/ContactStats';

const RandomContactPage = () => {
  const navigate = useNavigate();
  const [currentContact, setCurrentContact] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showMessageComposer, setShowMessageComposer] = useState(false);
  const [messageType, setMessageType] = useState('sms');
  const [filters, setFilters] = useState({
    category: '',
    lastContactDays: '',
    priority: '',
    includeBirthdays: false,
    excludeRecent: false,
    hasNotes: false,
    prioritizeWork: false,
    includeNoPhone: true,
    vipOnly: false,
    randomness: 50,
    priorityWeight: 30
  });
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({
    totalContacts: 247,
    contactsThisWeek: 8,
    averageResponseTime: 4,
    longestGap: 127
  });

  // Mock contacts data
  const mockContacts = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@techcorp.com",
      phone: "+1 (555) 123-4567",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      company: "TechCorp Solutions",
      category: "work",
      location: "San Francisco, CA",
      birthday: "March 15",
      lastContactDate: "2024-05-20",
      notes: `Sarah is our primary contact for the Q4 integration project. She mentioned wanting to discuss the new API specifications and timeline adjustments. Last conversation was about expanding the team for better project delivery.`,
      priority: "high"
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "m.chen@designstudio.com",
      phone: "+1 (555) 987-6543",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      company: "Creative Design Studio",
      category: "client",
      location: "New York, NY",
      birthday: "July 22",
      lastContactDate: "2024-03-15",
      notes: `Michael is working on the rebranding project for his startup. He's been very responsive and appreciates regular updates. Mentioned interest in long-term partnership opportunities.`,
      priority: "medium"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.r.personal@gmail.com",
      phone: "+1 (555) 456-7890",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      company: "",
      category: "personal",
      location: "Austin, TX",
      birthday: "November 8",
      lastContactDate: "2024-01-10",
      notes: `Emily is a college friend who moved to Austin for a new job. She loves hiking and photography. Last time we talked, she was excited about her new role at a local nonprofit organization.`,
      priority: "medium"
    },
    {
      id: 4,
      name: "David Thompson",
      email: "david.thompson@consulting.com",
      phone: "+1 (555) 321-0987",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      company: "Thompson Consulting",
      category: "networking",
      location: "Chicago, IL",
      birthday: "September 3",
      lastContactDate: "2023-11-22",
      notes: `David is a business consultant I met at the networking event last year. He has extensive experience in digital transformation and offered to provide insights for our upcoming projects.`,
      priority: "low"
    },
    {
      id: 5,
      name: "Lisa Park",
      email: "lisa.park@healthtech.com",
      phone: "+1 (555) 654-3210",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
      company: "HealthTech Innovations",
      category: "work",
      location: "Seattle, WA",
      birthday: "December 12",
      lastContactDate: "2024-06-30",
      notes: `Lisa leads the product team at HealthTech. She's been instrumental in our collaboration on the patient management system. Very detail-oriented and always provides valuable feedback.`,
      priority: "high"
    },
    {
      id: 6,
      name: "James Wilson",
      email: "james.wilson.family@gmail.com",
      phone: "+1 (555) 789-0123",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      company: "",
      category: "family",
      location: "Denver, CO",
      birthday: "April 18",
      lastContactDate: "2024-07-15",
      notes: `James is my cousin who recently moved to Denver for work. He's been exploring the local tech scene and asked for introductions to people in the industry. Very entrepreneurial mindset.`,
      priority: "medium"
    }
  ];

  // Mock history data
  const mockHistory = [
    {
      id: 1,
      contact: mockContacts?.[0],
      action: 'sent',
      messageType: 'email',
      messagePreview: 'Hi Sarah! Hope you\'re doing well. Just wanted to check in and see how the Q4 project is progressing...',
      timestamp: '2024-08-10T14:30:00Z'
    },
    {
      id: 2,
      contact: mockContacts?.[1],
      action: 'scheduled',
      messageType: 'sms',
      messagePreview: 'Hey Michael! Hope your rebranding project is going smoothly. Would love to catch up soon!',
      timestamp: '2024-08-09T10:15:00Z',
      scheduledFor: '2024-08-15T09:00:00Z'
    },
    {
      id: 3,
      contact: mockContacts?.[2],
      action: 'skipped',
      timestamp: '2024-08-08T16:45:00Z'
    },
    {
      id: 4,
      contact: mockContacts?.[3],
      action: 'sent',
      messageType: 'whatsapp',
      messagePreview: 'Hi David! It\'s been a while since we last connected. How have you been?',
      timestamp: '2024-08-07T11:20:00Z'
    }
  ];

  useEffect(() => {
    setHistory(mockHistory);
  }, []);

  const getFilteredContacts = () => {
    let filtered = [...mockContacts];

    // Apply category filter
    if (filters?.category) {
      filtered = filtered?.filter(contact => contact?.category === filters?.category);
    }

    // Apply last contact filter
    if (filters?.lastContactDays) {
      const daysAgo = parseInt(filters?.lastContactDays);
      const cutoffDate = new Date();
      cutoffDate?.setDate(cutoffDate?.getDate() - daysAgo);
      
      filtered = filtered?.filter(contact => {
        if (!contact?.lastContactDate) return true;
        return new Date(contact.lastContactDate) < cutoffDate;
      });
    }

    // Apply priority filter
    if (filters?.priority) {
      filtered = filtered?.filter(contact => contact?.priority === filters?.priority);
    }

    // Apply advanced filters
    if (filters?.excludeRecent) {
      const recentDate = new Date();
      recentDate?.setDate(recentDate?.getDate() - 7);
      filtered = filtered?.filter(contact => {
        if (!contact?.lastContactDate) return true;
        return new Date(contact.lastContactDate) < recentDate;
      });
    }

    if (filters?.hasNotes) {
      filtered = filtered?.filter(contact => contact?.notes && contact?.notes?.trim()?.length > 0);
    }

    if (filters?.vipOnly) {
      filtered = filtered?.filter(contact => contact?.priority === 'high');
    }

    if (!filters?.includeNoPhone) {
      filtered = filtered?.filter(contact => contact?.phone);
    }

    return filtered;
  };

  const selectRandomContact = () => {
    const filteredContacts = getFilteredContacts();
    
    if (filteredContacts?.length === 0) {
      alert('No contacts match your current filters. Please adjust your criteria.');
      return;
    }

    setIsLoading(true);
    
    // Simulate loading with animation
    setTimeout(() => {
      let selectedContact;
      
      if (filters?.priorityWeight > 50) {
        // Prioritize high-priority contacts
        const highPriorityContacts = filteredContacts?.filter(c => c?.priority === 'high');
        if (highPriorityContacts?.length > 0 && Math.random() < (filters?.priorityWeight / 100)) {
          selectedContact = highPriorityContacts?.[Math.floor(Math.random() * highPriorityContacts?.length)];
        }
      }
      
      // If no priority selection or random selection
      if (!selectedContact) {
        selectedContact = filteredContacts?.[Math.floor(Math.random() * filteredContacts?.length)];
      }
      
      setCurrentContact(selectedContact);
      setIsLoading(false);
    }, 1500);
  };

  const handleSendMessage = (type) => {
    setMessageType(type);
    setShowMessageComposer(true);
  };

  const handleMessageSent = (messageData) => {
    // Add to history
    const newHistoryItem = {
      id: Date.now(),
      contact: currentContact,
      action: messageData?.scheduled ? 'scheduled' : 'sent',
      messageType: messageData?.type,
      messagePreview: messageData?.content?.substring(0, 100),
      timestamp: new Date()?.toISOString(),
      scheduledFor: messageData?.scheduled ? `${messageData?.scheduleDate}T${messageData?.scheduleTime}:00Z` : null
    };

    setHistory(prev => [newHistoryItem, ...prev]);
    setShowMessageComposer(false);

    // Update stats
    setStats(prev => ({
      ...prev,
      contactsThisWeek: prev?.contactsThisWeek + 1
    }));

    // Show success message
    alert(`Message ${messageData?.scheduled ? 'scheduled' : 'sent'} successfully!`);
  };

  const handleSkipContact = () => {
    if (currentContact) {
      const skipHistoryItem = {
        id: Date.now(),
        contact: currentContact,
        action: 'skipped',
        timestamp: new Date()?.toISOString()
      };

      setHistory(prev => [skipHistoryItem, ...prev]);
    }
    
    selectRandomContact();
  };

  const handleViewDetails = () => {
    navigate('/contact-details', { state: { contact: currentContact } });
  };

  const handleContactClick = (contact) => {
    navigate('/contact-details', { state: { contact } });
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      category: '',
      lastContactDays: '',
      priority: '',
      includeBirthdays: false,
      excludeRecent: false,
      hasNotes: false,
      prioritizeWork: false,
      includeNoPhone: true,
      vipOnly: false,
      randomness: 50,
      priorityWeight: 30
    });
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear all history?')) {
      setHistory([]);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <BottomTabNavigation />
      {/* Main Content */}
      <div className="lg:ml-60 pb-16 lg:pb-0">
        {/* Header */}
        <StickyHeader 
          showSearch={false}
          showAddContact={false}
          onSettingsClick={() => navigate('/settings')}
        />

        {/* Breadcrumbs */}
        <NavigationBreadcrumbs />

        {/* Page Content */}
        <div className="p-4 lg:p-6 space-y-6">
          {/* Page Header */}
          <div className="text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start space-x-3 mb-4">
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                <Icon name="Shuffle" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                  Random Contact
                </h1>
                <p className="text-text-secondary">
                  Maintain relationships through serendipitous outreach
                </p>
              </div>
            </div>
          </div>

          {/* Main Action */}
          <div className="text-center">
            <Button
              variant="default"
              size="lg"
              onClick={selectRandomContact}
              disabled={isLoading}
              loading={isLoading}
              iconName="Shuffle"
              iconPosition="left"
              className="px-8 py-4 text-lg font-semibold hover-scale"
            >
              {isLoading ? 'Selecting Contact...' : 'Get Random Contact'}
            </Button>
            
            {getFilteredContacts()?.length > 0 && (
              <p className="text-sm text-text-secondary mt-2">
                {getFilteredContacts()?.length} contact{getFilteredContacts()?.length !== 1 ? 's' : ''} available
              </p>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Left Column - Contact Card & Message Composer */}
            <div className="xl:col-span-2 space-y-6">
              {/* Random Contact Card */}
              <RandomContactCard
                contact={currentContact}
                onSendMessage={handleSendMessage}
                onViewDetails={handleViewDetails}
                onSkip={handleSkipContact}
                isLoading={isLoading}
              />

              {/* Message Composer */}
              <MessageComposer
                contact={currentContact}
                messageType={messageType}
                onSend={handleMessageSent}
                onCancel={() => setShowMessageComposer(false)}
                isVisible={showMessageComposer}
              />

              {/* Filter Controls */}
              <FilterControls
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onReset={handleResetFilters}
              />
            </div>

            {/* Right Column - Stats & History */}
            <div className="space-y-6">
              {/* Contact Stats */}
              <ContactStats stats={stats} />

              {/* Recent History */}
              <RandomContactHistory
                history={history}
                onContactClick={handleContactClick}
                onClearHistory={handleClearHistory}
              />
            </div>
          </div>

          {/* Tips Section */}
          <div className="bg-surface border border-border rounded-xl p-6 shadow-card">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Lightbulb" size={20} color="white" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary mb-2">
                  Tips for Better Networking
                </h3>
                <ul className="text-sm text-text-secondary space-y-1">
                  <li>• Personalize your messages based on previous conversations</li>
                  <li>• Set a goal to contact 2-3 people per week consistently</li>
                  <li>• Use the scheduling feature to send messages at optimal times</li>
                  <li>• Keep notes updated after each interaction for better context</li>
                  <li>• Don't skip contacts too often - everyone deserves attention</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Mobile Bottom Navigation */}
      <BottomTabNavigation />
    </div>
  );
};

export default RandomContactPage;